import React, { useRef } from "react";

import i18n from "./config";
import en from "./locales/en.json";
import enProject from "./locales/ff/en.json";
import esProject from "./locales/ff/es.json";
import itProject from "./locales/ff/it.json";
import frProject from "./locales/ff/fr.json";
import deProject from "./locales/ff/de.json";
import { useFeatureFlag } from "@qualio/ui-components";
import { FeatureFlags } from "@design-controls/types";

/**
 * Recursive Partial. Typing the overrides against the base bundle means a
 * renamed or removed key in `locales/*.json` fails `yarn typecheck` instead of
 * silently becoming a no-op at runtime.
 */
type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

type Overrides = DeepPartial<typeof en>;

const PROJECT_TERMINOLOGY: Readonly<Record<string, Overrides>> = {
  en: enProject,
  es: esProject,
  it: itProject,
  fr: frProject,
  de: deProject,
};

const NAMESPACE = "translation";

/**
 * This is a *temporary* provider to change the i18n values based on a feature flag.
 * When removing this flag from the codebase, remember to also remove the orphaned
 * i18n files.
 */
export const TerminologyProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const ffEnabled = Boolean(
    useFeatureFlag()[FeatureFlags.LINK_RESOURCE_LIBRARY_PRODUCT],
  );
  const once = useRef(false);

  // Cant use an effect/layoutEffect hook here because we need this to affect children renders.
  if (ffEnabled && !once.current) {
    for (const [lng, resources] of Object.entries(PROJECT_TERMINOLOGY)) {
      // The trailing `true, true` means we only want to overwrite the exact
      // deep keys specified in our `resources` object.
      // See: https://www.i18next.com/overview/api#addresourcebundle
      i18n.addResourceBundle(lng, NAMESPACE, resources, true, true);
    }
    once.current = true;
  }

  return <>{children}</>;
};
